import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'mask'
})
export class MaskPipe implements PipeTransform {
  
  /**
   * Transform a value to masked format
   * @param value - The value to mask
   * @param visibleStart - Number of characters to show at start (default: 3)
   * @param visibleEnd - Number of characters to show at end (default: 1)
   * @param maskChar - Character to use for masking (default: 'X')
   * @param separator - Optional separator for formatting (e.g., '-' for Emirates ID)
   * @param isEmiratesId - Set to true to mask all characters for Emirates ID
   * @returns Masked string
   */
  transform(
    value: string, 
    visibleStart: number = 3, 
    visibleEnd: number = 1, 
    maskChar: string = 'X',
    separator?: string,
    isEmiratesId: boolean = false
  ): string {
    if (!value) return '';
    
    const cleanValue = value.replace(/[^0-9]/g, '');
    
    if (isEmiratesId || (separator === '-' && cleanValue.length >= 15)) {
      return this.formatEmiratesIdMask(cleanValue, maskChar, separator);
    }
    
    if (cleanValue.length <= visibleStart + visibleEnd) {
      return maskChar.repeat(cleanValue.length);
    }
    
    const start = cleanValue.slice(0, visibleStart);
    const end = cleanValue.slice(-visibleEnd);
    const maskedMiddle = maskChar.repeat(cleanValue.length - visibleStart - visibleEnd);
    
    let result = `${start}${maskedMiddle}${end}`;
    
    if (separator) {
      result = this.formatWithSeparator(result, separator);
    }
    
    return result;
  }
  
  /**
   * Format masked string with separators (e.g., for Emirates ID: XXX-XXXX-XXXXXXX-X)
   * @param value - The masked value
   * @param separator - The separator character
   * @returns Formatted string with separators
   */
  private formatWithSeparator(value: string, separator: string): string {
    if (value.length >= 15) {
      return `${value.slice(0, 3)}${separator}${value.slice(3, 7)}${separator}${value.slice(7, -1)}${separator}${value.slice(-1)}`;
    }
    
    return value.replace(/(.{4})/g, `$1${separator}`).replace(new RegExp(`${separator}$`), '');
  }

  /**
   * Format Emirates ID as completely masked with hyphens: XXXXXXXXXXXXXXXXXX -> XXX-XXXX-XXXXXXX-X
   * @param value - The numeric value (without separators)
   * @param maskChar - Character to use for masking
   * @param separator - The separator character
   * @returns Masked Emirates ID with separators
   */
  private formatEmiratesIdMask(value: string, maskChar: string, separator?: string): string {
    const mask = maskChar.repeat(18);
    
    if (separator === '-') {
      return `${mask.slice(0, 3)}-${mask.slice(3, 7)}-${mask.slice(7, 14)}-${mask.slice(14, 18)}`;
    }
    
    return mask;
  }
} 